import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import type { Role, Permission, CreateRoleDto, UpdateRoleDto, RoleCategory } from '../types';

// API functions
const fetchRoles = async (): Promise<Role[]> => {
  const response = await axiosInstance.get('/permissions/roles');
  return response.data;
};

const fetchRole = async (id: string): Promise<Role> => {
  const response = await axiosInstance.get(`/permissions/roles/${id}`);
  return response.data;
};

const fetchPermissions = async (): Promise<Permission[]> => {
  const response = await axiosInstance.get('/permissions');
  return response.data;
};

const createRole = async (data: CreateRoleDto): Promise<Role> => {
  const response = await axiosInstance.post('/permissions/roles', data);
  return response.data;
};

const updateRole = async ({ id, data }: { id: string; data: UpdateRoleDto }): Promise<Role> => {
  const response = await axiosInstance.patch(`/permissions/roles/${id}`, data);
  return response.data;
};

const deleteRole = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/permissions/roles/${id}`);
};

const cloneRole = async (id: string): Promise<Role> => {
  const response = await axiosInstance.post(`/permissions/roles/${id}/clone`);
  return response.data;
};

// Hooks
export const useRoles = () => {
  return useQuery({
    queryKey: ['roles'],
    queryFn: fetchRoles,
  });
};

export const useRole = (id: string) => {
  return useQuery({
    queryKey: ['roles', id],
    queryFn: () => fetchRole(id),
    enabled: !!id,
  });
};

export const usePermissions = () => {
  return useQuery({
    queryKey: ['permissions'],
    queryFn: fetchPermissions,
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateRole,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['roles', variables.id] });
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
};

export const useCloneRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cloneRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
};

// Helper to group permissions by resource
export const groupPermissionsByResource = (permissions: Permission[]) => {
  const grouped: Record<string, Permission[]> = {};

  for (const permission of permissions) {
    if (!grouped[permission.resource]) {
      grouped[permission.resource] = [];
    }
    grouped[permission.resource].push(permission);
  }

  return grouped;
};

// Resource display names
export const RESOURCE_LABELS: Record<string, string> = {
  products: 'Products',
  inventory: 'Inventory',
  sales: 'Sales',
  reservations: 'Reservations',
  staff: 'Staff',
  locations: 'Locations',
  reports: 'Reports',
  settings: 'Settings',
  customers: 'Customers',
  b2b: 'B2B',
  deliveries: 'Deliveries',
};

// Action display names
export const ACTION_LABELS: Record<string, string> = {
  read: 'View',
  create: 'Create',
  update: 'Edit',
  delete: 'Delete',
};

// Role category display names and descriptions
export const CATEGORY_LABELS: Record<RoleCategory, string> = {
  administrative: 'Administrative',
  operational: 'Operational',
  combined: 'Combined',
};

export const CATEGORY_DESCRIPTIONS: Record<RoleCategory, string> = {
  administrative: 'Organization management, staff, settings (e.g., org_admin)',
  operational: 'Day-to-day operations like sales, inventory (e.g., sales_operator)',
  combined: 'Convenience roles with mixed permissions (e.g., store_manager)',
};

// Helper to filter roles by category
export const getRolesByCategory = (roles: Role[], category: RoleCategory): Role[] => {
  return roles.filter((role) => role.category === category);
};

// Helper to group roles by category
// Note: Roles with null/undefined category default to 'combined'
export const groupRolesByCategory = (roles: Role[]): Record<RoleCategory, Role[]> => {
  return {
    administrative: roles.filter((r) => r.category === 'administrative'),
    operational: roles.filter((r) => r.category === 'operational'),
    combined: roles.filter((r) => r.category === 'combined' || !r.category),
  };
};

// ==================== Multi-Role User Assignment ====================

// API functions for user role assignment
const fetchUserRoles = async (userId: string): Promise<Role[]> => {
  const response = await axiosInstance.get(`/permissions/users/${userId}/roles`);
  return response.data;
};

const assignRolesToUser = async ({
  userId,
  roleIds,
}: {
  userId: string;
  roleIds: string[];
}): Promise<Role[]> => {
  const response = await axiosInstance.post(`/permissions/users/${userId}/roles`, {
    roleIds,
  });
  return response.data;
};

const addRoleToUser = async ({
  userId,
  roleId,
}: {
  userId: string;
  roleId: string;
}): Promise<Role[]> => {
  const response = await axiosInstance.post(`/permissions/users/${userId}/roles/add`, {
    roleId,
  });
  return response.data;
};

const removeRoleFromUser = async ({
  userId,
  roleId,
}: {
  userId: string;
  roleId: string;
}): Promise<Role[]> => {
  const response = await axiosInstance.delete(`/permissions/users/${userId}/roles/${roleId}`);
  return response.data;
};

// Hooks for user role assignment
export const useUserRoles = (userId: string) => {
  return useQuery({
    queryKey: ['userRoles', userId],
    queryFn: () => fetchUserRoles(userId),
    enabled: !!userId,
  });
};

export const useAssignRolesToUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignRolesToUser,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userRoles', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
};

export const useAddRoleToUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addRoleToUser,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userRoles', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
};

export const useRemoveRoleFromUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeRoleFromUser,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userRoles', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
  });
};
